import { memo, useEffect, useMemo, useRef, useState } from "react";
import { api, errorText } from "../api";
import { playLocal } from "../audio";
import { confirmDialog, promptDialog } from "../dialog";
import { useCompactControls } from "../hooks";
import { toast } from "../toast";
import type { Me, Preset, Row } from "../types";
import { EmptyState } from "./EmptyState";
import { IconButton, Icons, SearchIcon, type IconName } from "./Icons";
import { ActionSheet } from "./Sheet";
import { TimeAgo } from "./TimeAgo";

/* A request that has not been heard yet. `ready` is in here deliberately: the
   audio exists but the overlay has not played it, so from the streamer's side
   it is still coming. */
const PENDING = new Set(["queued", "generating", "ready"]);

export const isPending = (row: Row): boolean => PENDING.has(row.status);

/* What a pad on the soundboard says: its label, or the first few words. */
export function padName(row: Row): string {
  if (row.label) return row.label;
  const words = row.text.split(/\s+/).slice(0, 4).join(" ");
  return words.length < row.text.length ? `${words}…` : words;
}

/* Starring asks for the pad's name, suggesting the first words. Shared by the
   feed's star button and the preview strip's "save to soundboard". */
export async function starRow(row: Row): Promise<void> {
  const label = await promptDialog({
    title: "Add to the soundboard",
    body: `“${row.text}”`,
    confirmLabel: "Add",
    prompt: {
      label: "Name",
      placeholder: "Sub thanks",
      initial: padName(row).replace(/…$/, ""),
      maxLength: 32,
    },
  });
  if (label === null) return;
  await api(`/api/requests/${row.id}/star`, {
    method: "POST",
    body: JSON.stringify({ starred: true, label }),
  })
    .then(() => toast(`“${label || padName(row)}” is on the soundboard`, "ok"))
    .catch(fail);
}

export function unstarRow(row: Row): void {
  void api(`/api/requests/${row.id}/star`, {
    method: "POST",
    body: JSON.stringify({ starred: false }),
  })
    .then(() =>
      toast(`Removed “${padName(row)}” from the soundboard`, "", {
        label: "Undo",
        run: () =>
          void api(`/api/requests/${row.id}/star`, {
            method: "POST",
            body: JSON.stringify({ starred: true, label: row.label }),
          }).catch(fail),
      }),
    )
    .catch(fail);
}

const SHORT_KEYS: Record<string, string> = {
  use_speaker_boost: "boost",
  similarity_boost: "sim",
};

function settingBits(settings: Row["settings"]): string {
  return Object.entries(settings || {})
    .map(([key, value]) => `${SHORT_KEYS[key] ?? key}=${value}`)
    .join(" ");
}

const fail = (error: unknown) => toast(errorText(error), "error");

interface ItemProps {
  row: Row;
  me: Me;
  onReuse: (row: Row) => void;
  onMuted: () => void;
  /* Filter the history to this sender. Only the history passes it. */
  onUser?: (user: string) => void;
}

/* What can be done to one request.

   One list, rendered two ways. A mouse gets the icon strip it has always had;
   a thumb gets the same entries as a labelled sheet, because six 28px icons
   across a 390px row is both unhittable and unreadable — an icon with no
   tooltip is a guess, and "skip" and "mute" are not things to guess at. Built
   once so the two renderings cannot drift into offering different powers. */
interface RowAction {
  icon: IconName;
  /** The icon's tooltip, and the sheet's row. */
  label: string;
  /** The half of the tooltip that explains the consequence. */
  hint?: string;
  danger?: boolean;
  run: () => void;
}

function actionsFor(
  row: Row,
  me: Me,
  onReuse: (row: Row) => void,
  onMuted: () => void,
): RowAction[] {
  const mine = row.user === me.name;
  const list: RowAction[] = [];

  if (row.audio_url) {
    list.push({
      icon: "listen",
      label: "Listen here",
      run: () => playLocal(row.audio_url as string),
    });
    // A preview goes out through its own "send" button, which re-checks the
    // lock; the server refuses a replay of one for the same reason.
    if (row.status !== "held") {
      list.push({
        icon: "replay",
        label: "Replay on stream",
        hint: "free",
        run: () =>
          void api(`/api/requests/${row.id}/replay`, { method: "POST" }).catch(fail),
      });
    }
  }

  if (me.admin && row.audio_url) {
    list.push(
      row.starred
        ? {
            icon: "starred",
            label: "Remove from the soundboard",
            run: () => unstarRow(row),
          }
        : {
            icon: "star",
            label: "Add to the soundboard",

            run: () => void starRow(row),
          },
    );
  }

  list.push({
    icon: "reuse",
    label: "Copy into the composer",

    run: () => onReuse(row),
  });

  if (me.admin) {
    list.push(
      {
        icon: "skip",
        label: "Skip on stream",
        run: () =>
          void api(`/api/requests/${row.id}/skip`, { method: "POST" })
            .then(() =>
              /* Undo rather than a confirmation. This is one small icon among
                 six, pressed mid-stream, and the clip is still on disk — so the
                 cheap thing is to let it be taken back, not to charge every
                 deliberate skip a second click. */
              toast(`Skipped #${row.id}`, "", {
                label: "Undo",
                run: () =>
                  void api(`/api/requests/${row.id}/unskip`, { method: "POST" }).catch(
                    fail,
                  ),
              }),
            )
            .catch(fail),
      },
      {
        icon: "mute",
        label: `Mute ${row.user}`,

        danger: true,
        run: () =>
          void api("/api/admin/mute", {
            method: "POST",
            body: JSON.stringify({ user: row.user, muted: true }),
          })
            .then(onMuted)
            .catch(fail),
      },
    );
  }

  if (me.admin || mine) {
    list.push({
      icon: "remove",
      label: "Delete",

      danger: true,
      run: () =>
        void (async () => {
          const confirmed = await confirmDialog({
            title: `Delete #${row.id}?`,
            body: `“${row.text}”`,
            confirmLabel: "Delete",
            danger: true,
          });
          if (!confirmed) return;
          api(`/api/requests/${row.id}`, { method: "DELETE" }).catch(fail);
        })(),
    });
  }

  return list;
}

/* The action strip for one request — icons for a mouse, one "more" button and a
   labelled sheet for a thumb. Shared by the history rows and the compact queue
   rows, so the queue cannot quietly offer fewer powers than the history. */
function RowActions({ row, me, onReuse, onMuted }: ItemProps) {
  const touch = useCompactControls();
  const [sheetOpen, setSheetOpen] = useState(false);
  const actions = actionsFor(row, me, onReuse, onMuted);

  /* Listening stays on the row even on a phone: it is the one action pressed
     often enough that burying it behind a second tap would be felt, and the
     only one with no consequence if pressed by accident. */
  const inline = touch ? actions.filter((action) => action.icon === "listen") : actions;
  const overflow = touch ? actions.filter((action) => action.icon !== "listen") : [];

  return (
    <span className="actions">
      {inline.map((action) => (
        <IconButton
          key={action.label}
          icon={action.icon}
          title={action.hint ? `${action.label} (${action.hint})` : action.label}
          className={action.danger ? "danger" : ""}
          onClick={action.run}
        />
      ))}
      {overflow.length > 0 && (
        <>
          <button
            type="button"
            className="icon-btn row-more"
            aria-label={`What to do with #${row.id}`}
            aria-haspopup="dialog"
            onClick={() => setSheetOpen(true)}
          >
            {Icons.more}
          </button>
          <ActionSheet
            open={sheetOpen}
            title={`#${row.id} · ${row.user}`}
            actions={overflow}
            onClose={() => setSheetOpen(false)}
          />
        </>
      )}
    </span>
  );
}

const FeedItem = memo(function FeedItem({ row, me, onReuse, onMuted, onUser }: ItemProps) {
  const bits = settingBits(row.settings);

  return (
    <div className={`item status-${row.status}`}>
      <div className="top">
        {onUser ? (
          <button
            type="button"
            className="who who-filter"
            title={`Show only what ${row.user} said`}
            onClick={() => onUser(row.user)}
          >
            {row.user}
          </button>
        ) : (
          <span className="who">{row.user}</span>
        )}
        {row.starred && (
          <span className="pad-mark" title={`On the soundboard as “${padName(row)}”`}>
            {Icons.starred}
            <span className="sr-only">On the soundboard</span>
          </span>
        )}
        {/* "played" is what nearly every history row is, and thirty identical
            badges made the two that mattered — failed, skipped — harder to
            find. Only the exceptions are labelled. */}
        {row.status !== "played" && (
          <span className={`badge ${row.status}`}>{row.status}</span>
        )}
        <TimeAgo ts={row.created_at} />
        <span className="spacer" style={{ flex: 1 }}></span>
        <RowActions row={row} me={me} onReuse={onReuse} onMuted={onMuted} />
      </div>
      <div className="msg">{row.text}</div>
      <div className="meta">
        {`#${row.id} · ${row.voice_name} · ${row.model_id} · ${row.chars} chars` +
          (row.latency_ms ? ` · ${row.latency_ms}ms` : "") +
          (row.bytes ? ` · ${(row.bytes / 1024).toFixed(0)}kB` : "") +
          (bits ? ` · ${bits}` : "")}
      </div>
      {row.error && <div className="err">{row.error}</div>}
    </div>
  );
});

/* One line per waiting request. The queue lives in a box of fixed height next
   to what is playing, so a row has to be a row: the full history card with its
   meta line fits two of them, and the third would be a scrollbar. The whole
   text is in the tooltip, and the actions are the same ones the history has. */
const STATUS_WORDS: Record<string, string> = {
  queued: "waiting to generate",
  generating: "generating",
  ready: "ready to play",
};

export const QueueRow = memo(function QueueRow({ row, me, onReuse, onMuted }: ItemProps) {
  return (
    <>
      <span className={`qdot ${row.status}`} role="img" aria-label={STATUS_WORDS[row.status] ?? row.status}></span>
      <span className="who">{row.user}</span>
      <span className="qmsg">{row.text}</span>
      <RowActions row={row} me={me} onReuse={onReuse} onMuted={onMuted} />
    </>
  );
});

/* The queue as a list, so a screen reader hears it as one. */
export function QueueList({
  rows,
  me,
  onReuse,
  onMuted,
}: {
  rows: Row[];
  me: Me;
  onReuse: (row: Row) => void;
  onMuted: () => void;
}) {
  return (
    <div className="qlist" role="list" aria-label="Up next">
      {rows.map((row) => (
        <div key={row.id} role="listitem" className={`qrow status-${row.status}`} title={row.text}>
          <QueueRow row={row} me={me} onReuse={onReuse} onMuted={onMuted} />
        </div>
      ))}
    </div>
  );
}

interface Props {
  history: Row[];
  me: Me;
  onReuse: (row: Row) => void;
  onMuted: () => void;
}

/* What is about to be heard, oldest first — the order the overlay will play
   them in, which is the opposite of how history reads. Plural "now playing":
   with a browser source per character, several clips can be sounding at once,
   and every one of them belongs there rather than here. */
export function upNext(history: Row[], nowPlayingIds: number[]): Row[] {
  return history
    .filter((row) => isPending(row) && !nowPlayingIds.includes(row.id))
    .reverse();
}

/* The phone's queue list. It sits in its own segment of the Queue tab rather
   than above the history, so a message arriving never pushes the history down
   under somebody's thumb — and it is always there, empty or not, for the same
   reason. */
export function Queue({
  history,
  me,
  nowPlayingIds,
  onReuse,
  onMuted,
}: Props & { nowPlayingIds: number[] }) {
  const pending = upNext(history, nowPlayingIds);

  return (
    <div className="card queue-card">
      <h2>
        Up next <span className="spacer"></span>
        <span className="muted">{pending.length} waiting</span>
      </h2>
      <div className="queue">
        {pending.length === 0 ? (
          <EmptyState art="queue" title="Nothing waiting" />
        ) : (
          pending.map((row) => (
            <FeedItem
              key={row.id}
              row={row}
              me={me}
              onReuse={onReuse}
              onMuted={onMuted}
            />
          ))
        )}
      </div>
    </div>
  );
}

/* --------------------------------------------------------------- search */

type StatusFilter = "" | "failed" | "skipped";

interface Filters {
  q: string;
  user: string;
  status: StatusFilter;
  starred: boolean;
  preset: number | null;
}

const NO_FILTERS: Filters = { q: "", user: "", status: "", starred: false, preset: null };
const PAGE = 50;

function isFiltered(filters: Filters): boolean {
  return (
    filters.q.trim() !== "" ||
    filters.user !== "" ||
    filters.status !== "" ||
    filters.starred ||
    filters.preset !== null
  );
}

function queryString(filters: Filters, before: number | null): string {
  const params = new URLSearchParams({ limit: String(PAGE) });
  if (filters.q.trim()) params.set("q", filters.q.trim());
  if (filters.user) params.set("user", filters.user);
  if (filters.status) params.set("status", filters.status);
  if (filters.starred) params.set("starred", "true");
  if (filters.preset !== null) params.set("preset", String(filters.preset));
  if (before !== null) params.set("before", String(before));
  return params.toString();
}

/* Server-side, because the live list is a window of the last hundred-odd rows
   and the line somebody is looking for is usually older than that. While a
   search is showing, rows already in the results still update live — a clip
   that finishes playing stops saying "ready" — but new messages do not jump in
   among them, which would make a list somebody is reading scroll by itself. */
function useHistorySearch(filters: Filters, live: Row[]) {
  const [results, setResults] = useState<Row[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [more, setMore] = useState(false);
  const active = isFiltered(filters);
  const generation = useRef(0);
  const key = queryString(filters, null);

  useEffect(() => {
    if (!active) {
      generation.current++; // anything still in flight is for a search nobody wants
      setResults(null);
      setLoading(false);
      return;
    }
    const mine = ++generation.current;
    setLoading(true);
    const timer = window.setTimeout(() => {
      api<Row[]>(`/api/history?${key}`)
        .then((rows) => {
          if (mine !== generation.current) return; // a newer search won
          setResults(rows);
          setMore(rows.length === PAGE);
        })
        .catch(fail)
        .finally(() => mine === generation.current && setLoading(false));
    }, 250);
    return () => window.clearTimeout(timer);
  }, [active, key]);

  async function loadMore() {
    if (!results?.length) return;
    const mine = generation.current;
    setLoading(true);
    try {
      const rows = await api<Row[]>(
        `/api/history?${queryString(filters, results[results.length - 1].id)}`,
      );
      if (mine !== generation.current) return;
      setResults([...results, ...rows]);
      setMore(rows.length === PAGE);
    } catch (error) {
      fail(error);
    } finally {
      setLoading(false);
    }
  }

  const patched = useMemo(() => {
    if (!results) return null;
    const fresh = new Map(live.map((row) => [row.id, row]));
    return results.map((row) => fresh.get(row.id) ?? row);
  }, [results, live]);

  return { active, results: patched, loading, more, loadMore };
}

export function Feed({
  history,
  me,
  presets = [],
  onReuse,
  onMuted,
}: Props & { presets?: Preset[] }) {
  // A preview belongs to the strip above the composer until it is sent.
  const past = history.filter((row) => !isPending(row) && row.status !== "held");
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  const search = useHistorySearch(filters, history);
  const shown = search.active ? (search.results ?? []) : past;
  const set = (patch: Partial<Filters>) => setFilters((current) => ({ ...current, ...patch }));

  const chip = (label: string, on: boolean, toggle: () => void) => (
    <button type="button" className={"filter-chip" + (on ? " on" : "")} aria-pressed={on} onClick={toggle}>
      {label}
    </button>
  );

  return (
    <div className="card history-card">
      <h2>
        History <span className="spacer"></span>
        <span className="muted" aria-live="polite">
          {search.active
            ? search.loading && !search.results
              ? "searching…"
              : `${shown.length}${search.more ? "+" : ""} found`
            : `${past.length} shown`}
        </span>
      </h2>

      <div className="history-search" role="search">
        <label className="search-field">
          <SearchIcon />
          <span className="sr-only">Search the history</span>
          <input
            id="history-search"
            type="search"
            placeholder="Search history…"
            value={filters.q}
            maxLength={200}
            onChange={(event) => set({ q: event.target.value })}
            onKeyDown={(event) => {
              if (event.key === "Escape") setFilters(NO_FILTERS);
            }}
          />
        </label>
        <div className="filter-row" role="group" aria-label="Filters">
          {chip("Mine", filters.user === me.name, () =>
            set({ user: filters.user === me.name ? "" : me.name }),
          )}
          {filters.user && filters.user !== me.name && (
            <button
              type="button"
              className="filter-chip on"
              aria-label={`Showing only ${filters.user}; remove filter`}
              onClick={() => set({ user: "" })}
            >
              @{filters.user} <span aria-hidden="true">×</span>
            </button>
          )}
          {chip("Starred", filters.starred, () => set({ starred: !filters.starred }))}
          {chip("Failed", filters.status === "failed", () =>
            set({ status: filters.status === "failed" ? "" : "failed" }),
          )}
          {chip("Skipped", filters.status === "skipped", () =>
            set({ status: filters.status === "skipped" ? "" : "skipped" }),
          )}
          {presets.length > 0 && (
            <select
              className={"filter-select" + (filters.preset !== null ? " on" : "")}
              aria-label="Filter by character"
              value={filters.preset ?? ""}
              onChange={(event) =>
                set({ preset: event.target.value === "" ? null : Number(event.target.value) })
              }
            >
              <option value="">Any character</option>
              {presets.map((preset) => (
                <option key={preset.id} value={preset.id}>
                  {preset.name}
                </option>
              ))}
            </select>
          )}
          {search.active && (
            <button type="button" className="link" onClick={() => setFilters(NO_FILTERS)}>
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="feed">
        {shown.length === 0 ? (
          search.active ? (
            search.loading ? (
              <div className="muted">Searching…</div>
            ) : (
              <EmptyState
                art="search"
                title="Nothing matches"

                action={{ label: "Clear search", run: () => setFilters(NO_FILTERS) }}
              />
            )
          ) : (
            <EmptyState
              art="history"
              title="No messages yet"
            />
          )
        ) : (
          shown.map((row) => (
            <FeedItem
              key={row.id}
              row={row}
              me={me}
              onReuse={onReuse}
              onMuted={onMuted}
              onUser={(user) => set({ user })}
            />
          ))
        )}
        {search.active && search.more && (
          <button className="ghost load-more" disabled={search.loading} onClick={() => void search.loadMore()}>
            {search.loading ? "Loading…" : "Load more"}
          </button>
        )}
      </div>
    </div>
  );
}
